import React, { FC } from 'react';
import MuiAccordionSummary, { AccordionSummaryProps as MuiAccordionSummaryProps } from '@mui/material/AccordionSummary';
import { BpDesignSystem } from '../../types';
import { Icon } from '../Icon/Icon';
import { styled } from '../../styles';
import * as tokens from '@utmost.co/utmost-design-tokens';

type IconPosition = 'left' | 'right';
export type AccordionSummaryProps = BpDesignSystem.PropsWithChildren<
  MuiAccordionSummaryProps & {
    iconPosition?: IconPosition;
    fixedHeight?: boolean;
    minHeight?: number;
    hover?: boolean;
    disabled?: boolean;
    gap?: number;
    paddingInline?: number | `${number}px` | `${number}px ${number}px)`;
    paddingBlock?: number | `${number}px` | `${number}px ${number}px)`;
  }
>;

export const AccordionSummary: FC<AccordionSummaryProps> = ({
  dataTest = 'accordion-summary',
  iconPosition,
  fixedHeight = true,
  hover = true,
  minHeight,
  expandIcon = <Icon dataTest="accordion-toggle-button" name="keyboard_arrow_down" />,
  gap,
  paddingInline,
  paddingBlock,
  sx,
  ...props
}) => (
  <StyledAccordionSummary
    disableRipple={false}
    $iconPosition={iconPosition}
    $fixedHeight={fixedHeight}
    $hover={hover}
    $minHeight={minHeight}
    data-test={dataTest}
    expandIcon={expandIcon}
    sx={{
      gap,
      paddingInline,
      paddingBlock,
      flexDirection: iconPosition === 'left' ? 'row-reverse' : undefined,
      ...sx
    }}
    {...props}
    role="tab"
  />
);

export const StyledAccordionSummary = styled(MuiAccordionSummary)<{
  $iconPosition?: IconPosition;
  $minHeight?: number;
  $fixedHeight: boolean;
  $hover: boolean;
}>`
    min-height: ${({ $minHeight }) => $minHeight ?? 48}px !important;

    &:hover {
        background-color: ${({ $hover }) => ($hover ? tokens.stateOnSurfaceHoverStateLayerColor : undefined)};
    }
    
    .MuiAccordion-rounded & {
      border-radius: inherit;
    }
    
    .MuiAccordionSummary-content.Mui-expanded {
        margin: ${({ $fixedHeight }) => ($fixedHeight ? '12px 0' : undefined)};
    }
`;

StyledAccordionSummary.displayName = 'Accordion.Summary';
